/**
 * A fetch the browser aborted mid-flight. Almost always React Router cancelling
 * an in-flight loader because the user navigated away again (every
 * `prefetch="intent"` hover starts a real, abortable request), occasionally a
 * genuinely dropped connection. Each engine words it differently:
 *
 *   - Chromium (Chrome/Edge):  `Failed to fetch`
 *   - WebKit (Safari/iOS):     `Load failed`
 *   - Gecko (Firefox):         `NetworkError when attempting to fetch resource.`
 *
 * The Gecko variant is matched by prefix rather than by exact string. The
 * trailing full stop is part of the message today but is not guaranteed, and a
 * miss here is not cosmetic: it drops the user on the dead `<SomethingWrong />`
 * page instead of the self-healing `<NetworkError />` one. The prefix stops
 * short of the full stop but still keeps the whole phrase, so an unrelated
 * `NetworkError: ...` from a third-party library is not swept into a page that
 * auto-reloads and skips `crit` logging.
 *
 * Deliberately not gated on a Firefox user-agent test. The message is
 * Gecko-specific, so the UA check buys nothing, and it adds a second silent way
 * to fail for browsers that carry an unusual UA string.
 */
const GECKO_ABORT_PREFIX = 'NetworkError when attempting to fetch';

export const isNavigationAbortMessage = (message: string): boolean =>
    message === 'Failed to fetch' ||
    message === 'Load failed' ||
    isGeckoNavigationAbortMessage(message);

/** True for the Gecko wording specifically — the variant we sample rather than log in full. */
export const isGeckoNavigationAbortMessage = (message: string): boolean =>
    message.startsWith(GECKO_ABORT_PREFIX);

/**
 * Fraction of Gecko navigation aborts forwarded to `/remix-api/log`.
 *
 * Chromium and WebKit aborts are logged in full (~1,400 a month). The Gecko
 * variant was suppressed outright to stop a log flood, which also made a broken
 * UI invisible for fourteen months — so keep a thin, scalable stream instead of
 * nothing.
 *
 * `classifyRouterError` files Gecko and Chromium aborts under the same
 * `Failed to fetch` category, so a raw count of that category now blends two
 * sampling regimes. Sampled events carry `routerErrorSampleRate`; facet on it
 * before reading a Gecko volume, or the number is 10x low.
 */
export const GECKO_NAVIGATION_ABORT_SAMPLE_RATE = 0.1;

/** Randomly admit `rate` of events. Injectable for tests. */
export const shouldSampleNavigationAbort = (
    rate: number = GECKO_NAVIGATION_ABORT_SAMPLE_RATE,
    random: () => number = Math.random,
): boolean => random() < rate;
